import type { ReactNode } from 'react'
import { createContext, useContext, useMemo, useState } from 'react'

import { useCopySelectedOrdersModal } from '@/components/modals/CopySelectedOrdersModal'
import { useDeleteOrderModal } from '@/components/modals/DeleteOrderModal'
import { useDuplicateOrderModal } from '@/components/modals/DuplicateOrderModal'
import { type ExportOrderSource, useExportOrderModal } from '@/components/modals/ExportOrderModal'
import { useExportRoutesModal } from '@/components/modals/ExportRoutesModal'
import { OutOfStorageSpace } from '@/components/modals/OutOfStorageSpaceModal'
import { useSupportTicketModal } from '@/components/modals/SupportTicketModal'
import { SupportTicket } from '@/components/modals/SupportTicketModal/types'
import { useUnableToCreateRecurrentOrdersModal } from '@/components/modals/UnableToCreateRecurrentOrdersModal'
import { ModalPublicProps as UnableToCreateRecurrentOrdersModalPublicProps } from '../UnableToCreateRecurrentOrdersModal/types'

interface Props {
  children: ReactNode
}

interface Context {
  exportRoutes: {
    show: () => void
    close: () => void
  }
  exportOrders: {
    show: (
      source: ExportOrderSource,
      orderStepIds: string[],
      orderSteps: uui.domain.client.rm.ExtendedOrderStep[],
      date: string | undefined,
      planType: 'archived' | 'none' | 'simulation' | 'operations',
    ) => void
    close: () => void
  }
  supportTicket: {
    show: (ticket?: SupportTicket) => void
    close: () => void
  }
  unableToCreateRecurrentOrders: {
    show: (props: UnableToCreateRecurrentOrdersModalPublicProps) => void
    close: () => void
  }
  deleteOrders: {
    show: (orderStepIds: string[]) => void
    close: () => void
  }
  copySelectedOrders: {
    show: (orderStepIds: string[]) => void
    close: () => void
  }
  duplicateOrders: {
    show: (orderStepIds: string[]) => void
    close: () => void
  }
}

const noop = () => undefined

function getInitialContext(): Context {
  return {
    exportRoutes: {
      show: noop,
      close: noop,
    },
    exportOrders: {
      show: noop,
      close: noop,
    },
    supportTicket: {
      show: noop,
      close: noop,
    },
    unableToCreateRecurrentOrders: {
      show: noop,
      close: noop,
    },
    deleteOrders: {
      show: noop,
      close: noop,
    },
    copySelectedOrders: {
      show: noop,
      close: noop,
    },
    duplicateOrders: {
      show: noop,
      close: noop,
    },
  }
}

const context = createContext<Context>(getInitialContext())

export function ResidentModals(props: Props) {
  const { children } = props

  const [exportOrderSource, setExportOrderSource] = useState<ExportOrderSource>('default')
  const [supportTicket, setSupportTicket] = useState<SupportTicket>()
  const [unableToCreateRecurrenceOrderProps, setUnableToCreateRecurrenceOrderProps] =
    useState<UnableToCreateRecurrentOrdersModalPublicProps>({
      mode: 'create',
      onManageStorage: noop,
      onGoBackToEdit: noop,
      storageSaturationPercent: 0,
    })

  const [orderStepIdsToDelete, setOrderStepIdsToDelete] = useState<string[]>([])
  const [orderStepIdsToCopy, setOrderStepIdsToCopy] = useState<string[]>([])
  const [orderStepIdsToDuplicate, setOrderStepIdsToDuplicate] = useState<string[]>([])
  const [orderStepIdsToExport, setOrderStepIdsToExport] = useState<string[]>([])

  const [exportOrderSteps, setExportOrderSteps] = useState<
    uui.domain.client.rm.ExtendedOrderStep[]
  >([])
  const [exportOrderDate, setExportOrderDate] = useState<string | undefined>(undefined)
  const [exportOrderPlanType, setExportOrderPlanType] = useState<
    'archived' | 'none' | 'simulation' | 'operations'
  >('none')

  const {
    show: showExportRoutesModal,
    Modal: ExportRoutesModal,
    close: closeExportRoutesModal,
  } = useExportRoutesModal()

  const {
    show: showExportOrdersModal,
    Modal: ExportOrdersModal,
    close: closeExportOrdersModal,
  } = useExportOrderModal()

  const {
    show: showSupportTicketModal,
    Modal: SupportTicketModal,
    close: closeSupportTicketModal,
  } = useSupportTicketModal()

  const {
    show: showUnableToCreateRecurrenceOrderModal,
    Modal: UnableToCreateRecurrenceOrderModal,
    close: closeUnableToCreateRecurrenceOrderModal,
  } = useUnableToCreateRecurrentOrdersModal()

  const {
    show: showDeleteOrderModal,
    Modal: DeleteOrderModal,
    close: closeDeleteOrderModal,
  } = useDeleteOrderModal()

  const {
    show: showCopySelectedOrdersModal,
    Modal: CopySelectedOrdersModal,
    close: closeCopySelectedOrdersModal,
  } = useCopySelectedOrdersModal()

  const {
    show: showDuplicateOrderModal,
    Modal: DuplicateOrderModal,
    close: closeDuplicateOrderModal,
  } = useDuplicateOrderModal()

  const handleShowExportOrders = (
    source: ExportOrderSource,
    orderStepIds: string[],
    orderSteps: uui.domain.client.rm.ExtendedOrderStep[],
    date: string | undefined,
    planType: 'archived' | 'none' | 'simulation' | 'operations',
  ) => {
    setExportOrderSource(source)
    setOrderStepIdsToExport(orderStepIds)
    setExportOrderSteps(orderSteps)
    setExportOrderDate(date)
    setExportOrderPlanType(planType)
    showExportOrdersModal()
  }

  const handleCloseExportOrders = () => {
    setExportOrderSource('default')
    closeExportOrdersModal()
  }

  const handleSupportTicket = (ticket?: SupportTicket) => {
    setSupportTicket(ticket)
    showSupportTicketModal()
  }

  const handleDeleteOrders = (orderStepIds: string[]) => {
    setOrderStepIdsToDelete(orderStepIds)
    showDeleteOrderModal()
  }

  const handleCopySelectedOrders = (orderStepIds: string[]) => {
    setOrderStepIdsToCopy(orderStepIds)
    showCopySelectedOrdersModal()
  }

  const handleDuplicateOrders = (orderStepIds: string[]) => {
    setOrderStepIdsToDuplicate(orderStepIds)
    showDuplicateOrderModal()
  }

  const handleShowUnableToCreateRecurrenceOrderModal = (
    unableToCreateRecurrenceOrderProps: UnableToCreateRecurrentOrdersModalPublicProps,
  ) => {
    setUnableToCreateRecurrenceOrderProps(unableToCreateRecurrenceOrderProps)
    showUnableToCreateRecurrenceOrderModal()
  }

  const handleCloseUnableToCreateRecurrenceOrderModal = () => {
    closeUnableToCreateRecurrenceOrderModal()
  }

  const contextValue = useMemo<Context>(() => {
    return {
      exportRoutes: {
        show: showExportRoutesModal,
        close: closeExportRoutesModal,
      },
      exportOrders: {
        show: handleShowExportOrders,
        close: handleCloseExportOrders,
      },
      supportTicket: {
        show: handleSupportTicket,
        close: closeSupportTicketModal,
        modal: SupportTicketModal,
      },
      unableToCreateRecurrentOrders: {
        show: handleShowUnableToCreateRecurrenceOrderModal,
        close: handleCloseUnableToCreateRecurrenceOrderModal,
      },
      deleteOrders: {
        show: handleDeleteOrders,
        close: closeDeleteOrderModal,
      },
      copySelectedOrders: {
        show: handleCopySelectedOrders,
        close: closeCopySelectedOrdersModal,
      },
      duplicateOrders: {
        show: handleDuplicateOrders,
        close: closeDuplicateOrderModal,
      },
    }
  }, [
    showExportRoutesModal,
    closeExportRoutesModal,
    showExportOrdersModal,
    closeExportOrdersModal,
    handleSupportTicket,
    closeSupportTicketModal,
    handleDeleteOrders,
    closeDeleteOrderModal,
  ])

  return (
    <context.Provider value={contextValue}>
      {children}
      <ExportRoutesModal />
      <ExportOrdersModal
        source={exportOrderSource}
        orderStepIds={orderStepIdsToExport}
        orderSteps={exportOrderSteps}
        date={exportOrderDate}
        planType={exportOrderPlanType}
      />
      <SupportTicketModal ticket={supportTicket} />
      <OutOfStorageSpace />
      <UnableToCreateRecurrenceOrderModal {...unableToCreateRecurrenceOrderProps} />
      <DeleteOrderModal orderStepIds={orderStepIdsToDelete} />
      <CopySelectedOrdersModal orderStepIds={orderStepIdsToCopy} />
      <DuplicateOrderModal orderStepIds={orderStepIdsToDuplicate} />
    </context.Provider>
  )
}

export function useResidentModalsApi() {
  return useContext(context)
}
